import React, { useEffect, useMemo, useRef, useState } from "react";
import { Calculator, Info } from "lucide-react";
import { formatCurrency } from "utils/helperFunctions";
import DatePicker from "components/financial/DatePicker";
import Table from "components/ui/Table";
import {
  useGetProductEconomicsQuery,
  useGetCostBreakdownQuery,
} from "../apis/reports";

// Frontend key → backend product string
const PRODUCT_KEY_MAP = {
  subscription: "Subscription",
  sandy: "Sandy Campaign",
  photoEditing: "Photo Editing",
  sms: "SMS",
};

const PRODUCT_CATEGORIES = [
  {
    key: "subscription",
    label: "Subscription",
    title: "Base subscription",
    blurb:
      "Subscription usage is a shared pool. It is divided by active subscription users. Direct Sandy campaign costs are excluded.",
    usersLabel: "Active subscription users",
    unitLabel: "Cost of 1 subscription",
    chargeLabel: "Charge at least",
  },
  {
    key: "sandy",
    label: "Sandy Campaign",
    title: "Sandy campaign",
    blurb:
      "Direct Sandy campaign and assistant costs. Divided by campaigns (or deals) that used Sandy in the period.",
    usersLabel: "Active Sandy campaigns",
    unitLabel: "Cost of 1 Sandy campaign",
    chargeLabel: "Charge at least",
  },
  {
    key: "photoEditing",
    label: "Photo Editing",
    title: "Photo editing",
    blurb:
      "Photo editing related costs. Divided by photo editing jobs in the period.",
    usersLabel: "Active photo editing jobs",
    unitLabel: "Cost of 1 photo edit",
    chargeLabel: "Charge at least",
  },
  {
    key: "sms",
    label: "SMS",
    title: "SMS",
    blurb:
      "SMS costs shared across the platform. Divided by active subscription users.",
    usersLabel: "Active subscription users",
    unitLabel: "Cost of 1 SMS seat",
    chargeLabel: "Charge at least",
  },
];

const buildCurrentMonth = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return {
    startDate: `${y}-${m}-01`,
    endDate: `${y}-${m}-${String(last).padStart(2, "0")}`,
  };
};

const PROVIDER_DESCRIPTIONS = {
  openai: "AI writing and analysis",
  gemini: "AI search and insights",
  anthropic: "AI conversations",
  twilio: "Subscription SMS",
  intellisms: "Subscription SMS",
  ayrshare: "Social posting profiles",
};

const Segmented = ({ options, value, onChange }) => (
  <div className="inline-flex items-center gap-0.5 border border-mediumgray bg-white rounded p-0.5">
    {options.map((opt) => (
      <button
        key={opt.key}
        type="button"
        onClick={() => onChange(opt.key)}
        className={`whitespace-nowrap ${
          value === opt.key ? "black-button" : "outline-button"
        }`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const MetricCard = ({ label, value, hint, emphasize }) => (
  <div
    className={`rounded-lg border p-4 ${
      emphasize
        ? "bg-emerald-700 border-emerald-700 text-white"
        : "bg-white border-mediumgray"
    }`}
  >
    <div
      className={`text-[10px] font-semibold uppercase tracking-wider ${
        emphasize ? "text-emerald-100" : "text-darkgray"
      }`}
    >
      {label}
    </div>
    <div
      className={`mt-1 text-2xl font-semibold tabular-nums ${
        emphasize ? "text-white" : "text-darkergray"
      }`}
    >
      {value}
    </div>
    {hint ? (
      <div className={`mt-1 text-xs ${emphasize ? "text-emerald-100" : "text-darkgray"}`}>
        {hint}
      </div>
    ) : null}
  </div>
);

const Field = ({ label, value, onChange, suffix }) => (
  <label className="block">
    <span className="form-label">{label}</span>
    <div className="flex items-center gap-2">
      <input
        type="number"
        min="0"
        step="any"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="form-input border border-mediumgray max-w-[160px] tabular-nums"
      />
      {suffix ? <span className="text-xs text-darkgray">{suffix}</span> : null}
    </div>
  </label>
);

const USAGE_COLUMNS = [
  {
    accessorKey: "label",
    header: "Cost",
    cell: (row) => <span className="font-medium text-darkergray">{row.label}</span>,
    isSortable: false,
  },
  {
    accessorKey: "provider",
    header: "What it is for",
    cell: (row) => (
      <span className="text-darkgray">
        {PROVIDER_DESCRIPTIONS[row.provider] ?? row.provider}
      </span>
    ),
    isSortable: false,
  },
  {
    accessorKey: "cost",
    header: "Estimated",
    cell: (row) => (
      <span className="tabular-nums font-semibold text-red-500">
        {formatCurrency(row.cost ?? 0)}
      </span>
    ),
    isSortable: false,
  },
];

const OTHER_COLUMNS = [
  {
    accessorKey: "name",
    header: "Name",
    cell: (row) => <span className="font-medium text-darkergray">{row.name}</span>,
    isSortable: false,
  },
  {
    accessorKey: "category",
    header: "Category",
    cell: (row) => <span className="text-darkgray">{row.category}</span>,
    isSortable: false,
  },
  {
    accessorKey: "sharePercent",
    header: "Share",
    // Under 100% when the category's cost is split across products.
    cell: (row) =>
      row.sharePercent != null && row.sharePercent < 100 ? (
        <span className="tabular-nums text-darkgray">
          {row.sharePercent}% of {formatCurrency(row.fullAmount)}
        </span>
      ) : (
        <span className="text-darkgray">100%</span>
      ),
    isSortable: false,
  },
  {
    accessorKey: "amount",
    header: "Amount",
    cell: (row) => (
      <span className="tabular-nums font-semibold text-red-500">
        {formatCurrency(row.amount)}
      </span>
    ),
    isSortable: false,
  },
];

const NETWORK_COLUMNS = [
  {
    accessorKey: "name",
    header: "Network / Org",
    cell: (row) => <span className="font-semibold text-darkergray">{row.name}</span>,
    isSortable: false,
  },
  {
    accessorKey: "activeSubs",
    header: "Active subs",
    cell: (row) => (
      <span className="tabular-nums text-darkergray">
        {row.activeSubs.toLocaleString("en-AU")}
      </span>
    ),
    isSortable: false,
  },
  {
    accessorKey: "costAud",
    header: "Total cost",
    cell: (row) => (
      <span className="tabular-nums font-semibold text-red-500">
        {formatCurrency(row.costAud)}
      </span>
    ),
    isSortable: false,
  },
  {
    accessorKey: "costPerSub",
    header: "Cost / sub",
    cell: (row) => (
      <span className="tabular-nums font-semibold text-darkergray">
        {row.costPerSub != null ? formatCurrency(row.costPerSub) : "—"}
      </span>
    ),
    isSortable: false,
  },
];

const ProductEconomics = ({ productKey, rangeParams }) => {
  const meta = PRODUCT_CATEGORIES.find((p) => p.key === productKey);
  const backendProduct = PRODUCT_KEY_MAP[productKey];

  const { data, isPending, isError } = useGetProductEconomicsQuery(
    { ...rangeParams, product: backendProduct },
    !!backendProduct && Object.keys(rangeParams).length > 0
  );

  const [users, setUsers] = useState("");
  const [price, setPrice] = useState("99");
  const [marginWanted, setMarginWanted] = useState("30");

  const prevKeyRef = useRef(null);
  useEffect(() => {
    if (data?.subscriptionCount == null) return;
    const key = `${productKey}|${JSON.stringify(rangeParams)}`;
    if (prevKeyRef.current !== key) {
      prevKeyRef.current = key;
      setUsers(String(data.subscriptionCount));
    }
  }, [data, productKey, rangeParams]);

  const totalUsage = data?.totalUsageCost ?? 0;
  const totalFixed = data?.totalOtherCost ?? 0;

  const calc = useMemo(() => {
    const n = Math.max(Number(users) || 0, 0);
    const p = Math.max(Number(price) || 0, 0);
    const m = Math.max(Number(marginWanted) || 0, 0) / 100;
    const usagePerUser = n > 0 ? totalUsage / n : 0;
    const fixedPerUser = n > 0 ? totalFixed / n : 0;
    const unitCost = usagePerUser + fixedPerUser;
    const chargeAtLeast = m < 1 ? unitCost / (1 - m) : unitCost;
    const marginAtPrice = p > 0 ? ((p - unitCost) / p) * 100 : 0;
    return { n, usagePerUser, fixedPerUser, unitCost, chargeAtLeast, marginAtPrice, p };
  }, [users, price, marginWanted, totalUsage, totalFixed]);

  const usageRows = data?.usageCostRows ?? [];
  const otherRows = data?.otherCostRows ?? [];

  return (
    <div className="space-y-5">
      <div className="bg-white border border-mediumgray rounded-lg p-5">
        <div className="flex items-start gap-2 mb-1">
          <Calculator className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
          <div>
            <h3 className="text-base text-darkergray">{meta.title}</h3>
            <p className="text-sm text-darkgray mt-0.5 max-w-2xl">{meta.blurb}</p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label={meta.usersLabel} value={users} onChange={setUsers} />
          <Field label="Price you charge" value={price} onChange={setPrice} suffix="AUD" />
          <Field label="Profit you want" value={marginWanted} onChange={setMarginWanted} suffix="%" />
        </div>

        {isPending ? (
          <div className="mt-4 h-10 animate-pulse rounded-md bg-lightgray" />
        ) : isError ? (
          <div className="mt-4 rounded-md bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-700">
            Failed to load cost data. Check the backend connection.
          </div>
        ) : (
          <div className="mt-4 flex items-start gap-2 rounded-md bg-cyan-50 border border-cyan-100 px-3 py-2.5 text-sm text-cyan-900">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              {formatCurrency(totalUsage)} {meta.label.toLowerCase()} usage
              {calc.n > 0 ? (
                <>
                  {" "}
                  ÷ {calc.n.toLocaleString("en-AU")} ={" "}
                  <strong>{formatCurrency(calc.usagePerUser)}</strong> per user.
                </>
              ) : (
                <> — enter users to see per-user cost.</>
              )}
            </span>
          </div>
        )}

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <MetricCard label="Usage per user" value={formatCurrency(calc.usagePerUser)} hint="Usage ÷ active users" />
          <MetricCard label="Fixed cost per user" value={formatCurrency(calc.fixedPerUser)} hint="Fixed cost ÷ active users" />
          <MetricCard label={meta.unitLabel} value={formatCurrency(calc.unitCost)} hint="Usage + fixed share" />
          <MetricCard
            label={meta.chargeLabel}
            value={formatCurrency(calc.chargeAtLeast)}
            hint={`For ${Number(marginWanted) || 0}% profit`}
            emphasize
          />
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-darkgray mb-1.5">
            <span>Profit margin at {formatCurrency(calc.p || 0)}</span>
            <span
              className={`font-semibold tabular-nums ${
                calc.marginAtPrice >= 0 ? "text-emerald-700" : "text-red-600"
              }`}
            >
              {Number.isFinite(calc.marginAtPrice) ? `${calc.marginAtPrice.toFixed(1)}%` : "—"}
            </span>
          </div>
          <div className="h-2 rounded-full bg-lightgray overflow-hidden">
            <div
              className={`h-full rounded-full ${
                calc.marginAtPrice >= 0 ? "bg-emerald-600" : "bg-red-500"
              }`}
              style={{ width: `${Math.min(Math.max(calc.marginAtPrice, 0), 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Usage Costs */}
      <div className="bg-white border border-mediumgray rounded-lg overflow-hidden">
        <div className="px-5 py-4 border-b border-lightgray">
          <h3 className="uppercase tracking-wider text-red-600">
            {meta.label} — usage costs
          </h3>
          <p className="text-sm text-darkgray mt-0.5">
            Costs pulled automatically from provider logs. Only rows tagged to{" "}
            <strong className="font-medium text-darkergray">{meta.label}</strong> are included.
          </p>
        </div>
        <Table
          columns={USAGE_COLUMNS}
          data={usageRows}
          searchable={false}
          pageSize={50}
          emptyText="No costs found for this product and period. Make sure ProductTags are configured."
        />
        {!isPending && usageRows.length > 0 && (
          <div className="px-5 py-3 border-t border-mediumgray flex justify-end">
            <span className="text-xs font-semibold uppercase tracking-wide text-darkgray mr-4">
              Total usage costs
            </span>
            <span className="tabular-nums font-semibold text-darkergray">
              {formatCurrency(totalUsage)}
            </span>
          </div>
        )}
      </div>

      {/* Other / Fixed Costs */}
      {!isPending && otherRows.length > 0 && (
        <div className="bg-white border border-mediumgray rounded-lg overflow-hidden">
          <div className="px-5 py-4 border-b border-lightgray">
            <h3 className="uppercase tracking-wider text-red-600">
              {meta.label} — other costs
            </h3>
            <p className="text-sm text-darkgray mt-0.5">
              Manually entered expenses tagged to{" "}
              <strong className="font-medium text-darkergray">{meta.label}</strong>,
              at its share when a category is split across products.
            </p>
          </div>
          <Table
            columns={OTHER_COLUMNS}
            data={otherRows}
            searchable={false}
            pageSize={50}
          />
          <div className="px-5 py-3 border-t border-mediumgray flex justify-end">
            <span className="text-xs font-semibold uppercase tracking-wide text-darkgray mr-4">
              Total other costs
            </span>
            <span className="tabular-nums font-semibold text-darkergray">
              {formatCurrency(totalFixed)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const CostBreakdown = ({ productKey, rangeParams }) => {
  const backendProduct = PRODUCT_KEY_MAP[productKey];

  const { data, isPending, isError } = useGetCostBreakdownQuery(
    { ...rangeParams, product: backendProduct },
    !!backendProduct && Object.keys(rangeParams).length > 0
  );

  const networks = data?.networks ?? [];
  const unattributed = data?.unattributedCostAud ?? 0;
  const breakdownTotal = networks.reduce((s, n) => s + n.costAud, 0) + unattributed;

  const tableData = useMemo(() => {
    const rows = [...networks];
    if (unattributed > 0) {
      rows.push({
        networkId: "__unattributed__",
        name: "Unattributed",
        activeSubs: 0,
        costAud: unattributed,
        costPerSub: null,
        orgs: [],
      });
    }
    return rows;
  }, [networks, unattributed]);

  return (
    <div className="bg-white border border-mediumgray rounded-lg overflow-hidden">
      <div className="px-5 py-4 border-b border-lightgray">
        <h3 className="uppercase tracking-wider text-darkergray">
          Cost breakdown by network
        </h3>
        <p className="text-sm text-darkgray mt-0.5">
          AI + SMS costs attributed per network and org. Click a network to see org-level detail.
        </p>
      </div>
      {isError ? (
        <div className="px-5 py-6 text-center text-sm text-red-600">
          Failed to load breakdown data.
        </div>
      ) : (
        <>
          <Table
            columns={NETWORK_COLUMNS}
            data={tableData}
            searchable={false}
            pageSize={50}
            emptyText="No data found. Tag some AI features or SMS purposes to this product first."
            isRowExpandable={(row) => row.orgs?.length > 0}
            renderExpanded={(row) => (
              <div className="bg-lightergray/40 border-t border-lightgray">
                <table className="w-full text-sm">
                  <tbody>
                    {row.orgs.map((org) => (
                      <tr key={org.orgId} className="border-b border-lightgray last:border-0">
                        <td className="px-5 py-2.5 pl-12 text-darkgray">{org.name}</td>
                        <td className="px-5 py-2.5 text-right tabular-nums text-darkgray">
                          {org.activeSubs.toLocaleString("en-AU")}
                        </td>
                        <td className="px-5 py-2.5 text-right tabular-nums text-red-400">
                          {formatCurrency(org.costAud)}
                        </td>
                        <td className="px-5 py-2.5 text-right tabular-nums text-darkgray">
                          {org.costPerSub != null ? formatCurrency(org.costPerSub) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          />
          {tableData.length > 0 && (
            <div className="px-5 py-3 border-t-2 border-mediumgray flex justify-between items-center bg-lightergray">
              <span className="font-semibold text-darkergray">Total</span>
              <span className="tabular-nums font-bold text-red-600">
                {formatCurrency(breakdownTotal)}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const Reports = () => {
  const [productKey, setProductKey] = useState("subscription");
  const [dateValue, setDateValue] = useState(buildCurrentMonth);

  const rangeParams = useMemo(() => {
    const { startDate, endDate } = dateValue;
    if (startDate && endDate) return { startDate, endDate };
    return {};
  }, [dateValue]);

  return (
    <div className="container mx-auto p-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5">
        <div>
          <h1 className="tracking-widest uppercase text-darkergray">Reports</h1>
          <p className="text-sm text-darkgray mt-0.5">Product economics</p>
        </div>
      </div>

      <div className="space-y-5">
        <div className="bg-white border border-mediumgray rounded-lg p-5">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex items-start gap-2">
              <Calculator className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
              <div>
                <h2 className="text-base text-darkergray">Product economics</h2>
                <p className="text-sm text-darkgray mt-0.5">
                  Choose one revenue type. See its cost, price, and profit margin.
                </p>
              </div>
            </div>
            <DatePicker value={dateValue} onChange={setDateValue} />
          </div>
          <div className="mt-4">
            <Segmented
              value={productKey}
              onChange={setProductKey}
              options={PRODUCT_CATEGORIES.map((p) => ({ key: p.key, label: p.label }))}
            />
          </div>
        </div>

        <ProductEconomics productKey={productKey} rangeParams={rangeParams} />
        <CostBreakdown productKey={productKey} rangeParams={rangeParams} />
      </div>
    </div>
  );
};

export default Reports;
